import { Injectable } from '@angular/core';
import { ClientUrl, environment } from '@environment';
import * as moment from 'moment';
import { rutFormat } from 'rut-helpers';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { KeyLogs, KeyMensajesSalida } from 'src/app/shared/enums/log.enum';
import { IConfiguracionVariables, IVariables } from 'src/app/shared/interfaces/common.interface';
import { DialogService } from 'src/app/shared/services';
import { LoggerService } from 'src/app/shared/services/logs/logger.service';
import { XAuthService } from 'src/app/shared/services/xauth/xauth.service';
import { messageType } from '../../../shared/components/user-message/user-message.component';
import { getInitialsFromName, toTitleCase } from '../../../shared/helpers/utils';
import { AppService } from '../../../shared/services/app/app.service';
import { ExecutiveService } from '../../../shared/services/executive/executive.service';
import { LoadingService } from '../../../shared/services/loading/loading.service';
import { VisualizadorClientesComponent } from '../../components/visualizador-clientes/visualizador-clientes.component';
import { ClientSiteRequestModel } from '../../models/client-summary/client-data.model';
import { ClientBalanceModel } from '../../models/client-summary/client-summary.model';
import { ContactInfo, DataClientModel, Product } from '../../models/client-summary/data-client.model';
import { ProfitabilityItem } from '../../models/client-summary/profitability.model';
import { BusinessLineType, ClientRequestService } from '../client-request/client-request.service';
import { ISubFeatureVars } from 'src/app/shared/interfaces/feature-vars.interface';
import { IFichaClienteGeneral } from '@shared/interfaces/ficha-cliente.interface';

@Injectable()
export class ClientService {
    public searchResult = new BehaviorSubject<UserMessage>(null);

    private readonly productsMap = new Map<string, string>([
        ['AFP', 'AFP'],
        ['FFMM', 'FFMM'],
        ['Vida', 'INSURANCES'],
        ['Corredora', 'CORREDOR_BOLSA'],
        // ['RIA', 'RIA'],
    ]);

    constructor(
        private xAuthService: XAuthService,
        private clientRequestService: ClientRequestService,
        private loadingService: LoadingService,
        private executive: ExecutiveService,
        private appService: AppService,
        private dialogService: DialogService,
        private loggerService: LoggerService,
    ) {}

    getDataClient(): Observable<DataClientModel> {
        return combineLatest([
            this.clientRequestService.clientData.asObservable(),
            this.clientRequestService.clientAccionInversion,
            this.clientRequestService.clientBalances,
            this.clientRequestService.clientExecutive,
            this.clientRequestService.companyInfo,
            this.clientRequestService.signMandate,
            this.clientRequestService.personInfo,
            this.clientRequestService.documents,
            this.clientRequestService.statusPassword,
            this.clientRequestService.lastSesion,
            this.clientRequestService.executiveName,
        ]).pipe(
            filter(([data]) => data !== null),
            map(([data, accionInversion, balances, clientExecutive, companyInfo, signMandate, personInfo, documents, statusPass, ultimaSesion, getExecutiveName]) => {
                const products = this.getExistingProducts(balances);
                const profileType = accionInversion?.perfilInversionista?.Perfil_Prev || 'Sin perfil';
                const fechaPrev = accionInversion?.perfilInversionista?.fecha_Prev;
                const profileDate = fechaPrev ? moment(accionInversion?.perfilInversionista?.fecha_Prev, 'MM/DD/YYYY hh:mm:ss A').format('DD/MM/YYYY HH:mm:ss A') : null;
                const companyInfoData: ContactInfo[] = this.getCompanyInfo(companyInfo);
                const ejecutivo = getExecutiveName?.data?.ejecutivo ?? {};
                const { nombreEjecutivo = "", esEjecutivoAFP = false } = ejecutivo || {};
                let executiveName = !esEjecutivoAFP ? nombreEjecutivo: "Sin Ejecutivo .";

                if(documents){
                    const tipoPersona =sessionStorage.getItem("tipoPersona");
                    const CNCG380PJ = sessionStorage.getItem("CNCG380PJ") == 'S' ? true : false;
                    const CNCG380 = sessionStorage.getItem("CNCG380") == 'S' ? true : false;
                    documents.ccg380 = tipoPersona == 'JURIDICA'?CNCG380PJ : CNCG380;
                }

                if(ejecutivo) {
                    const configuracionVarsStr = sessionStorage.getItem('configuracionVariables');

                    if(configuracionVarsStr) {
                        const configuracionVariables: IConfiguracionVariables = JSON.parse(configuracionVarsStr);
                        const { PORTAL_ASESORES_CONDICIONES }: IVariables = configuracionVariables?.variables || {};
                        const { codigosSucursales, nombreEjecutivoSD } = PORTAL_ASESORES_CONDICIONES.subFeatureVars.reduce((acc: any, curr: ISubFeatureVars) => {
                            if(curr.estado !== 'ACTIVO') return acc;
                            if(curr.codigo === 'TEXT_EJECUTIVO_SURA_DIRECTO') acc['nombreEjecutivoSD'] = curr.valor;
                            if(curr.codigo === 'CODIGOS_SUCURSALES_SURA_DIRECTO') acc['codigosSucursales'] = curr.valor;
                            return acc;
                        }, { codigosSucursales: null, nombreEjecutivoSD: null });
                        const codigosSucursalesSD = (codigosSucursales ? codigosSucursales.split(';') : []).map((code: string) => code?.toUpperCase()?.trim());

                        if(codigosSucursalesSD.includes(ejecutivo?.sucursal?.toUpperCase()?.trim())) executiveName = nombreEjecutivoSD;
                    }
                }
                this.setDatosEjecutivo(ejecutivo);

                let lastSession = '';
                if (ultimaSesion && ultimaSesion.length > 0) {
                    lastSession = ultimaSesion[0].fecha;
                }
                // * If the object is empty, there was an error in the loadData call in client-request.service
                const isPersonInfoEmpty = !Object.keys(personInfo || {})?.length;
                const emailClient = isPersonInfoEmpty ? 'Email no disponible' : personInfo?.email;
                const phoneClient = isPersonInfoEmpty ? 'Teléfono no disponible' : personInfo?.telefono;

                const datosPortafolio = accionInversion?.poseeFichacliente?.dataOptimus?.SocioNegocio[0]?.DatosPortafolio;
                const IdTipoBloqueo = accionInversion?.poseeFichacliente?.dataOptimusNueva?.SocioNegocio[0]?.IdTipoBloqueo;
                const FechaDefuncion = accionInversion?.poseeFichacliente?.dataOptimusNueva?.SocioNegocio[0]?.FechaDefuncion;

                const passObj = {
                    glosaTipoClave: '',
                    codigoEstadoClave: '',
                    glosaEstadoClave: '',
                };

                statusPass = statusPass
                    ? statusPass.map((pass) => {
                          pass.glosaTipoClave = pass.glosaTipoClave.replace('CLAVE', '');
                          return pass;
                      })
                    : [passObj, passObj];

                return this.formatResponseDataClient({
                    data,
                    accionInversion,
                    clientExecutive,
                    documents,
                    statusPass,
                    products,
                    profileType,
                    executiveName,
                    companyInfoData,
                    signMandate,
                    lastSession,
                    isPersonInfoEmpty,
                    emailClient,
                    phoneClient,
                    datosPortafolio,
                    IdTipoBloqueo,
                    FechaDefuncion,
                    profileDate,
                });
            }),
        );
    }

    public formatResponseDataClient(allData: any): DataClientModel {
        const {
            data,
            accionInversion,
            clientExecutive,
            documents,
            statusPass,
            products,
            profileType,
            executiveName,
            companyInfoData,
            signMandate,
            lastSession,
            IdTipoBloqueo,
            FechaDefuncion,
            isPersonInfoEmpty,
            emailClient,
            phoneClient,
            datosPortafolio,
            profileDate,
        } = allData;

        const indicadoresCliente = accionInversion?.poseeFichacliente?.dataOptimus?.SocioNegocio?.[0]?.IndicadorCliente ?? [];
        const isFotocopiaOk = indicadoresCliente.find(i => i.CodigoTipoIndicador === 'FOTOCOPIA')?.ValorIndicador === 'S';
        const tieneContratoFw = this.tieneMarcaContratoFw(accionInversion?.poseeFichacliente) && !this.validarProductosAFPVida(products);
        sessionStorage.setItem("tieneContratoFw", JSON.stringify(tieneContratoFw));

        return {
            rut: rutFormat(`${data.RutCliente}-${data.DigitoVerificador}`),
            name: data.NombreCliente,
            initials: getInitialsFromName(data.NombreCliente),
            lastSesion: lastSession,
            IdTipoBloqueo: IdTipoBloqueo,
            FechaDefuncion: FechaDefuncion,
            clientInfo: [
                {
                    icon: 'mail',
                    text: emailClient,
                    href: emailClient && !isPersonInfoEmpty ? `mailTo:${emailClient}` : null,
                    sinInfo: isPersonInfoEmpty,
                    tipo: 'correo',
                },
                {
                    icon: 'phone',
                    text: phoneClient,
                    href: phoneClient && !isPersonInfoEmpty ? `tel:${phoneClient}` : null,
                    sinInfo: isPersonInfoEmpty,
                    tipo: 'telefono',
                },
            ],
            profile: {
                type: profileType,
                state: accionInversion?.perfilInversionista?.HEADER === '00',
                error: accionInversion === null,
                date: profileDate,
            },
            segment: clientExecutive === null ? 'No disponible' : JSON.parse(sessionStorage.getItem('segmento'))?.nombre ?? 'Sin segmento',
            companyInfo: companyInfoData,
            executiveName,
            products,
            documentation: [
                {
                    key: 'ticketClient',
                    label: 'Ficha Cliente',
                    isOkey: accionInversion?.poseeFichacliente?.tieneFicha,
                    result: accionInversion?.poseeFichacliente?.tieneFicha ? 'Actualizado' : 'Pendiente',
                },
                {
                    key: 'ccg',
                    label: 'CCG (380)',
                    isOkey: documents?.ccg380,
                    result: documents?.ccg380 ? 'Actualizado' : 'Pendiente',
                },
                {
                    key: 'mandateAfp',
                    label: 'Mandato AFP',
                    isOkey: signMandate?.mandatoSuscrito,
                    result: signMandate?.mandatoSuscrito ? 'Firmado' : 'Sin Firmar',
                    isStateNoMandato: signMandate?.estado == 'NoMandato' ? false : true,
                },
                {
                    key: 'planApv',
                    label: 'Plan APV',
                    isOkey: documents?.planApv,
                    result: documents?.planApv ? 'Actualizado' : 'Sin Firmar',
                },
                {
                    key: 'identidad',
                    label: 'Doc. identidad',
                    isOkey: isFotocopiaOk,
                    result: isFotocopiaOk ? 'Actualizado' : 'Pendiente',
                },
                {
                    key: 'contratoFw',
                    label: 'Cto. Forward',
                    isOkey: tieneContratoFw,
                    result: tieneContratoFw ? 'Firmado' : 'Sin Firmar',
                },
            ],
            statusPaswword: statusPass,
            datosPortafolio,
        };
    }

    private getCompanyInfo(companyInfo): ContactInfo[] {
        sessionStorage.removeItem('rutRepresentanteLegal');
        if (companyInfo) {
            const telefonoEmpresa = companyInfo.telefonosEmpresa.telefonoEmpresa;
            const telefono = !Array.isArray(telefonoEmpresa) ? telefonoEmpresa : telefonoEmpresa[0];
            const emailEmpresa = companyInfo.emailsEmpresa?.emailEmpresa;
            const email = !Array.isArray(emailEmpresa) ? emailEmpresa?.correo : this.obtenerCorreoEmpresa(emailEmpresa);
            let companyPhone = null;
            if (telefono) {
                companyPhone = telefono.codigoPais + telefono.codigoArea + telefono.numero;
            }
            const rutRepresentanteLegal = companyInfo?.representanteLegal?.rut;
            if (rutRepresentanteLegal?.dv && rutRepresentanteLegal?.numero) {
                const { dv, numero } = rutRepresentanteLegal ?? {};
                sessionStorage.setItem('rutRepresentanteLegal', `${numero}-${dv}`);
            }
            return [
                {
                    text: companyInfo.representanteLegal.nombre + ' ' + companyInfo.apellidoPaterno + ' ' + companyInfo.apellidoMaterno,
                    icon: 'person',tipo: 'persona',
                },
                {
                    text: rutFormat(`${companyInfo.representanteLegal.rut.numero}-${companyInfo.representanteLegal.rut.dv}`),
                    icon: 'folder_shared', tipo: 'carpeta_compartida',
                },
                {
                    text: email,
                    icon: 'mail',
                    href: email ? `mailTo:${email}` : null,
                    sinInfo: email ? !email?.trim() : true,
                    tipo: 'correo',
                },
                {
                    text: companyPhone,
                    icon: 'phone',
                    href: companyPhone ? `tel:${companyPhone}` : null,
                    sinInfo: companyPhone ? !companyPhone?.trim() : true,
                    tipo: 'telefono',
                },
            ];
        }
        return null;
    }

    private getExecutiveName(nombre: string, apellido: string): string {
        if (typeof nombre === 'string' && typeof apellido === 'string') {
            return `${nombre} ${apellido}`;
        }
        return 'Sin información';
    }

    private getExistingProducts(balances: ClientBalanceModel[] | null): Product[] {
        return Array.from(this.productsMap.keys()).map((p) => {
            const found = balances?.find((item) => item.type === this.productsMap.get(p));
            return {
                displayName: p,
                exists: !!found,
                error: balances === null,
            };
        });
    }

    async goToSiteClient(client: Client, rutaCliente: string, prod?: any) {
        this.loadingService.showLoading();

        const executiveModel = this.executive.executiveModel;
        let clientProfile = 'Sin Perfil';
        this.getDataClient()
            .pipe(take(1))
            .subscribe((data) => {
                clientProfile = data.profile.type;
            })
            .unsubscribe();
        const segmento = JSON.parse(sessionStorage.getItem('segmento'));
        const payload: ClientSiteRequestModel = {
            FechaNacimiento: null,
            RutCliente: client.rut,
            Nombre: client.nombre,
            NombreEjecutivo: sessionStorage.getItem('NombreEjecutivoAsesor') ?? executiveModel.executiveName,
            rutEjecutivoAsesor: executiveModel.executiveRut,
            ApellidoPaterno: '',
            ApellidoMaterno: '',
            TipoUsuario: 'Ejecutivo',
            MandatoSuscrito: false,
            TipoAsesor: executiveModel.executiveProfile.profileName,
            PerfilAsesor: executiveModel.executiveProfile,
            sesion: sessionStorage.getItem('sesionAsesor') ?? 'S/I',
            cuentaAD: sessionStorage.getItem('cuentaAD'),
            segMdw: {
                idSegmento: segmento.codigo,
                segmento: segmento.nombre
            }
        };

        const executiveUsername = payload.NombreEjecutivo;
        this.xAuthService.generarXToken(payload).then((resp: any) => {
            const { token, xtoken } = resp?.data;
            const realToken = xtoken ?? token;
            let locationUrl = `${ClientUrl}/auth?t=${realToken}&ruta=${rutaCliente}&perfilCliente=${clientProfile}&rutEjecutivoAsesor=${payload.rutEjecutivoAsesor}&executiveUsername=${executiveUsername}`;
            if (prod !== undefined) {
                const extras = `​&name=${prod.codigoCb}​&amount=${prod.amount}&type=${prod.type}&prevision=${prod.prevision}&order=${prod.order}`;
                locationUrl = `${locationUrl}${extras}`;
            } else {
                locationUrl = `${ClientUrl}/auth?t=${realToken}&ruta=${rutaCliente}&perfilCliente=${clientProfile}&rutEjecutivoAsesor=${payload.rutEjecutivoAsesor}&executiveUsername=${executiveUsername}`;
            }
            this.openVisualizadorCliente(locationUrl, payload, clientProfile, realToken);
            this.loadingService.hideLoading();
        });
    }

    async irAWFI(client: Client, prevision: string, origen: string) {
        this.loadingService.showLoading();
        const executiveModel = this.executive.executiveModel;

        const payload: ClientSiteRequestModel = {
            FechaNacimiento: null,
            RutCliente: client.rut,
            Nombre: client.nombre,
            NombreEjecutivo: sessionStorage.getItem('NombreEjecutivoAsesor') ?? executiveModel.executiveName,
            rutEjecutivoAsesor: executiveModel.executiveRut,
            ApellidoPaterno: '',
            ApellidoMaterno: '',
            TipoUsuario: 'Ejecutivo',
            MandatoSuscrito: false,
            TipoAsesor: executiveModel.executiveProfile.profileName,
            PerfilAsesor: executiveModel.executiveProfile,
            sesion: sessionStorage.getItem('sesionAsesor') ?? 'S/I',
            cuentaAD: sessionStorage.getItem('cuentaAD'),
        };

        this.xAuthService.generarXToken(payload).then((resp: any) => {
            const { xtoken } = resp?.data;
            const { URL, PATHS } = environment.WFI;
            let locationUrl = `${URL}/${PATHS.AUTH}?t=${xtoken}&prevision=${prevision}&origen=${origen}`;
            window.location.href = locationUrl;
            const detalleLog = {
                payload,
                prevision,
                origen,
                locationUrl: `${URL}/${PATHS.AUTH}`,
            };
            this.loggerService.generarLog(KeyLogs.WFI_INGRESO_PORTAL_ASESOR_INVERSION, KeyMensajesSalida.ASUNTO_INGRESO_PORTAL_ASESOR_INVERSION, detalleLog);
        });
    }

    openVisualizadorCliente(urlAuth, datosCliente, clienteProfile, xtoken = null) {
        const config = {
            disableClose: true,
            maxWidth: '95%',
            width: '95%',
            height: '95%',
            data: { urlAuth, datosCliente, clienteProfile, xtoken },
        };

        const value = this.dialogService.openDialog(VisualizadorClientesComponent, config);
    }

    getTransitBalance() {
        return this.clientRequestService.clientBalances.pipe(
            map((balance) => {
                return balance?.filter((data) => data?.amountInTransit > 0);
            }),
        );
    }

    getProfitability(): Observable<ProfitabilityItem[]> {
        return this.clientRequestService.profitability?.pipe(
            map((data) => {
                if (data?.rentabilidad?.length > 0) {
                    const rentabilidad = data.rentabilidad;
                    const [ytd, monthly, lastThreeMonth, lastTwelveMonth] = rentabilidad;
                    const monthName = data.monthName ?? 'mensual';

                    return [
                        {
                            label: toTitleCase(monthName),
                            balances: monthly?.Monto,
                            rentability: monthly?.Porciento,
                        },
                        {
                            label: 'Últ. 3 meses',
                            balances: lastThreeMonth?.Monto,
                            rentability: lastThreeMonth?.Porciento,
                        },
                        {
                            label: 'Últ. 12 meses',
                            balances: lastTwelveMonth?.Monto,
                            rentability: lastTwelveMonth?.Porciento,
                        },
                        {
                            label: 'YTD',
                            balances: ytd?.Monto,
                            rentability: ytd?.Porciento,
                        },
                    ];
                }
                return [];
            }),
        );
    }

    async getProductDetail(params): Promise<any> {
        return this.clientRequestService.getProductDetail(params);
    }

    async getCertificateMonth(rut: string, fecha: string, lineaNegocio: BusinessLineType): Promise<any> {
        return this.clientRequestService.getCertificateMonth(rut, fecha, lineaNegocio);
    }

    obtenerCorreoEmpresa(emailEmpresa) {
        let correo = '';
        const emailEncontrado = emailEmpresa?.find((data) => data.estadoEmail === '1');
        if (emailEncontrado) {
            correo = emailEncontrado?.correo;
        }
        return correo;
    }

    private setDatosEjecutivo(ejecutivo: any): void {
        const datosEjecutivo = JSON.parse(sessionStorage.getItem('dataExecutive'));

        if (datosEjecutivo && ejecutivo) {
            const { MailEjecutivo = '', MailEjecutivoAntiguo = null, TelefonoEjecutivo = '', TelefonoEjecutivoAntiguo = null } = datosEjecutivo;
            const rutEjecutivo = ejecutivo?.rutEjecutivo?.toUpperCase();
            const rutAsesor = `${datosEjecutivo?.RutEjecutivo}-${datosEjecutivo?.DvEjecutivo}`?.toUpperCase() || '';
            const configuracionVariables: IConfiguracionVariables = JSON.parse(sessionStorage.getItem('configuracionVariables'));
            const { PORTAL_ASESORES_CONDICIONES }: IVariables = configuracionVariables?.variables || {};
            let rutsEjecutivoSura = [];
            PORTAL_ASESORES_CONDICIONES?.subFeatureVars.forEach((v: ISubFeatureVars) => {
                if (v.estado !== 'ACTIVO') return;
                if (v.codigo === 'RUTS_EJECUTIVO_SURA') {
                    rutsEjecutivoSura = v.valor.split(';').map((rut: string) => rut.toUpperCase());
                }
            });
            const rutCoincide = rutEjecutivo && (rutsEjecutivoSura.includes(rutEjecutivo) || rutAsesor === rutEjecutivo);

            if (rutCoincide) {
              const { email = '', areaTelefono = '', telefono = '', areaMovil = '', movil = '' } = ejecutivo;
              const telefonoFijo = `${areaTelefono}${telefono}`.trim();
              const telefonoMovil = `${areaMovil}${movil}`.trim();
              const telefonoEjecutivo = telefonoFijo || telefonoMovil;
              datosEjecutivo.MailEjecutivo = email || MailEjecutivo;
              datosEjecutivo.TelefonoEjecutivo = telefonoEjecutivo || TelefonoEjecutivo;
            } else {
              datosEjecutivo.MailEjecutivo = MailEjecutivoAntiguo;
              datosEjecutivo.TelefonoEjecutivo = TelefonoEjecutivoAntiguo;
            }
            sessionStorage.setItem('dataExecutive', JSON.stringify(datosEjecutivo));
        }
    }

    private tieneMarcaContratoFw(fichaCliente: IFichaClienteGeneral): boolean {
    try {
      const socios = fichaCliente?.dataOptimus?.SocioNegocio ?? [];
      return socios.some(
        (socio) =>
          socio.TipoEntidad === 'CLI' &&
          (socio.IndicadorCliente ?? []).some(
            (ind) => ind.CodigoTipoIndicador === 'CCF' && ind.ValorIndicador === 'S',
          ),
      );
    } catch (error) {
      return false;
    }
  }

  private validarProductosAFPVida(products: Product[]): boolean {
    const tipoProductos = products?.filter((p) => p.exists).map((p) => p.displayName) ?? [];
    return tipoProductos?.length && tipoProductos.every((tipo) => tipo?.toUpperCase() === 'AFP' || tipo?.toUpperCase() === 'VIDA');
  }
}

export interface UserMessage {
    isError?: boolean;
    useTitle?: string;
    useMessage?: string;
    mainMessage?: string;
    userMessageType?: messageType;
}

export interface Client {
    rut: string;
    nombre: string;
    apellidoPaterno: string;
    apellidoMaterno: string;
}
